// Article metadata section rendered after document content.

import React from 'react';
import { Link } from 'react-router';
import {
  formatBlogTag,
  isValidBlogTag,
  type Document,
} from '@airhead-games/shared/content';
import { formatUtcDateForUserLocale } from '@airhead-games/shared/utilities';
import ShareActions from '@/content/rendering/chrome/ShareActions';
import { Badge } from '@/components/ui/badge';

interface ArticleMetaSectionProps {
  document: Document;
}

const formatTagLabel = (tag: string): string =>
  isValidBlogTag(tag) ? formatBlogTag(tag) : tag;

const ArticleMetaSection: React.FC<ArticleMetaSectionProps> = ({
  document,
}) => {
  if (document.kind !== 'blog' && document.kind !== 'report') {
    return null;
  }

  const publishedAt = formatUtcDateForUserLocale(document.meta?.publishedAt, {
    month: 'long',
  });
  const items = [
    document.meta?.author
      ? { label: 'Author', value: document.meta.author }
      : undefined,
    publishedAt ? { label: 'Published', value: publishedAt } : undefined,
    document.meta?.dataRange
      ? {
          label: 'Data Range',
          value: `${document.meta.dataRange.start} to ${document.meta.dataRange.end}`,
        }
      : undefined,
  ].filter((item): item is { label: string; value: string } => Boolean(item));

  return (
    <section
      aria-label="Article metadata"
      className="border-t border-border py-8"
    >
      <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
        {items.map((item) => (
          <div key={item.label} className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {item.label}
            </span>
            <span className="text-sm leading-6">{item.value}</span>
          </div>
        ))}
        {document.meta?.tags && document.meta.tags.length > 0 && (
          <div className="flex flex-col gap-2 sm:col-span-2 xl:col-span-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Topics
            </span>
            <div className="flex flex-wrap gap-2">
              {document.meta.tags.map((tag) => (
                <Badge key={tag} variant="secondary" asChild>
                  <Link to={`/blog/category/${tag}`}>
                    {formatTagLabel(tag)}
                  </Link>
                </Badge>
              ))}
            </div>
          </div>
        )}
        <div className="flex flex-col gap-2 sm:col-span-2 xl:col-span-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Share
          </span>
          <ShareActions document={document} />
        </div>
      </div>
    </section>
  );
};

export default ArticleMetaSection;
